/**
 * Proof-of-delivery display logic for a stop's delivery orders.
 *
 * A PO with multiple line items is stored as multiple delivery_orders rows
 * that all carry the same POD capture (signature/photos), so the raw rows
 * can't be rendered 1:1 — shared media must be shown once per unique capture
 * while each line's outcome note is kept.
 */

/** Minimal shape of a delivery_orders row this module reads. */
export interface PodOrderRow {
  id: number | string;
  order_number: string;
  delivery_status?: string | null;
  delivered_at?: string | null;
  pod_signer_name?: string | null;
  pod_signature_url?: string | null;
  pod_photo_urls?: unknown;
  pod_workflow_data?: unknown;
}

/** One renderable POD entry: the row supplying the header, plus deduped media. */
export interface PodBlock {
  order: PodOrderRow;
  note: string | null;
  showSignature: boolean;
  /** Photo URLs not already shown by an earlier block at this stop. */
  photos: string[];
}

export const outcomeNote = (o: PodOrderRow): string | null => {
  const wf =
    typeof o?.pod_workflow_data === 'string'
      ? (() => {
          try {
            return JSON.parse(o.pod_workflow_data);
          } catch {
            return null;
          }
        })()
      : o?.pod_workflow_data;
  return (wf as any)?.outcome?.note ?? null;
};

/** POD photo URLs — pod_photo_urls is jsonb (array), but tolerate a stringified array. */
export const podPhotoUrls = (o: PodOrderRow): string[] => {
  const raw = o?.pod_photo_urls;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
};

export const hasPod = (o: PodOrderRow): boolean =>
  !!o.delivered_at || !!o.pod_signer_name || !!outcomeNote(o) || !!o.pod_signature_url || podPhotoUrls(o).length > 0;

/**
 * Collapse a stop's delivery orders into renderable POD blocks.
 *
 * Signatures and photos are deduped independently across the stop (a row with
 * signature + photos and a sibling row with photos only share one capture).
 * A block whose header (order # + signer + delivered time) repeats an earlier
 * block and contributes no note and no new media is dropped entirely.
 */
export function buildPodBlocks(orders: PodOrderRow[]): PodBlock[] {
  const seenSigs = new Set<string>();
  const seenPhotos = new Set<string>();
  const seenHeaders = new Set<string>();
  const blocks: PodBlock[] = [];

  for (const o of orders.filter(hasPod)) {
    const note = outcomeNote(o);
    const showSignature = !!o.pod_signature_url && !seenSigs.has(o.pod_signature_url);
    if (o.pod_signature_url) seenSigs.add(o.pod_signature_url);
    const photos = podPhotoUrls(o).filter(url => !seenPhotos.has(url));
    photos.forEach(url => seenPhotos.add(url));
    const headerKey = `${o.order_number}|${o.pod_signer_name || ''}|${o.delivered_at || ''}`;
    const isDuplicateHeader = seenHeaders.has(headerKey);
    seenHeaders.add(headerKey);
    if (isDuplicateHeader && !note && !showSignature && photos.length === 0) continue;
    blocks.push({ order: o, note, showSignature, photos });
  }

  return blocks;
}
